import type { GrantPoc } from '../types/grant'

const GRANT_MIRROR_KEY = 'canvasser_grant'

export interface GrantMirrorCampaign {
  id: string
  label: string
}

export interface GrantMirror {
  grantId: string
  kind: string
  subjectContactId: string | null
  ownerCommitteeId: string
  /** [Canvasser redesign Phase 1] So the header + waiting room render offline (spec §6.1). */
  campaign?: GrantMirrorCampaign | null
  /** [Phase 1] The campaign Point of Contact. Organizer-entered STAFF contact info (spec §13 Q5:
   *  NonPii) — allowed here. The CANVASSER's own name is never mirrored. */
  poc?: GrantPoc | null
}

function pickCampaign(c: unknown): GrantMirrorCampaign | null {
  if (!c || typeof c !== 'object') return null
  const { id, label } = c as Record<string, unknown>
  return typeof id === 'string' && typeof label === 'string' ? { id, label } : null
}

function pickPoc(p: unknown): GrantPoc | null {
  if (!p || typeof p !== 'object') return null
  const { name, phone, email } = p as Record<string, unknown>
  const str = (v: unknown) => (typeof v === 'string' ? v : null)
  return { name: str(name), phone: str(phone), email: str(email) }
}

export function readGrantMirror(): GrantMirror | null {
  const raw = localStorage.getItem(GRANT_MIRROR_KEY)
  if (!raw) return null
  try {
    const p = JSON.parse(raw)
    return {
      grantId: p.grantId,
      kind: p.kind,
      subjectContactId: p.subjectContactId ?? null,
      ownerCommitteeId: p.ownerCommitteeId,
      campaign: pickCampaign(p.campaign),
      poc: pickPoc(p.poc),
    }
  } catch {
    return null
  }
}

export function writeGrantMirror(m: GrantMirror): void {
  // Ids + campaign label + staff POC only — never the canvasser's own name, never a token
  // (frontend CLAUDE.md security rule). Pick fields explicitly so a caller passing a wider object
  // (e.g. the full GrantIdentity, which also carries subjectFirstName/subjectLastName) can never
  // leak PII into localStorage via structural typing.
  const { grantId, kind, subjectContactId, ownerCommitteeId } = m
  localStorage.setItem(GRANT_MIRROR_KEY, JSON.stringify({
    grantId, kind, subjectContactId, ownerCommitteeId,
    campaign: pickCampaign(m.campaign), poc: pickPoc(m.poc),
  }))
}

export function clearGrantMirror(): void {
  localStorage.removeItem(GRANT_MIRROR_KEY)
}
